import React, { useRef, useEffect, useState } from 'react';
import { Input } from "@/components/ui/input";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect?: (lat: number, lng: number, postalCode: string) => void;
  placeholder?: string;
  className?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onLocationSelect,
  placeholder = "Διεύθυνση",
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const listenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const [manualPostalCode, setManualPostalCode] = useState<string | null>(null);
  const [locationDetails, setLocationDetails] = useState<{
    cityName?: string;
    regionName?: string;
  }>({});

  useEffect(() => {
    const loadScript = async () => {
      try {
        await loadGoogleMapsScript();
        setIsLoaded(true);
      } catch (error) {
        console.error("Error loading Google Maps script:", error);
      }
    };

    loadScript();
  }, []);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;

    const options = {
      componentRestrictions: { country: "gr" },
      fields: ["address_components", "geometry", "formatted_address"],
    };

    autocompleteRef.current = new google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    const handlePlaceSelect = () => {
      if (!autocompleteRef.current) return;

      const place = autocompleteRef.current.getPlace();
      // Check if we've got a valid place with geometry
      if (!place.geometry || !place.geometry.location) {
        console.warn("No geometry information available for this place");
        
        // If the input looks like a postal code, city name, or locality, try geocoding it directly
        const currentValue = value.trim();
        const postalCodeRegex = /^\d{5}$/;
        
        if (postalCodeRegex.test(currentValue)) {
          // Postal code only input
          geocodePostalCode(currentValue);
        } else if (currentValue.length > 2) {
          // Attempt to geocode any non-empty input as it might be a city or region name
          geocodeAddress(currentValue);
        }
        return;
      }

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      let postalCode = "";
      let cityName = "";
      let regionName = "";

      // Extract postal code and locality information from address components
      if (place.address_components) {
        for (const component of place.address_components) {
          if (component.types.includes("postal_code")) {
            postalCode = component.long_name;
          }
          if (component.types.includes("locality")) {
            cityName = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            regionName = component.long_name;
          }
        }
      }

      console.log(`Selected location: ${place.formatted_address}, Lat: ${lat}, Lng: ${lng}, Postal: ${postalCode}, City: ${cityName}, Region: ${regionName}`);

      // Update the input value with the formatted address
      if (place.formatted_address) {
        onChange(place.formatted_address);
      }

      // Store location details for possible fallback matching
      setLocationDetails({
        cityName,
        regionName
      });

      // Callback with location data
      if (onLocationSelect) {
        onLocationSelect(lat, lng, postalCode);
      }
      
      // Save postal code for potential future use
      setManualPostalCode(postalCode);
    };

    // Add the event listener and store the reference
    listenerRef.current = autocompleteRef.current.addListener("place_changed", handlePlaceSelect);

    // Improve form submission prevention
    const preventSubmit = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && document.activeElement === inputRef.current) {
        e.preventDefault();
        e.stopPropagation();
        
        // When Enter is pressed, explicitly trigger place selection
        if (autocompleteRef.current) {
          const place = autocompleteRef.current.getPlace();
          if (!place || !place.geometry) {
            // If no place is selected yet, try to geocode the current input value
            geocodeAddress(value.trim());
            return false;
          }
        }
        return false;
      }
    };
    
    // Apply to the document level to ensure it catches all Enter events when input is focused
    document.addEventListener('keydown', preventSubmit, true);
    
    // Also apply directly to the input element
    if (inputRef.current) {
      inputRef.current.addEventListener('keydown', preventSubmit, true);
    }

    return () => {
      // Clean up autocomplete on unmount
      if (listenerRef.current && google.maps.event) {
        google.maps.event.removeListener(listenerRef.current);
      }
      
      // Remove event listeners
      document.removeEventListener('keydown', preventSubmit, true);
      if (inputRef.current) {
        inputRef.current.removeEventListener('keydown', preventSubmit, true);
      }
    };
  }, [isLoaded, onChange, onLocationSelect, value]);

  // Function to handle geocoding any address string (city, region, etc.)
  const geocodeAddress = async (address: string) => {
    if (!isLoaded || !address || !window.google) return;
    
    console.log("Attempting to geocode address:", address);
    
    try {
      const geocoder = new google.maps.Geocoder();
      
      geocoder.geocode(
        { 
          address: address + ", Greece", // Add country to improve accuracy
          componentRestrictions: { country: "gr" } 
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            processGeocodingResults(results[0], address);
          } else {
            console.error("Failed to geocode address:", status);
          }
        }
      );
    } catch (error) {
      console.error("Error in address geocoding:", error);
    }
  };

  // Function to handle manual postal code geocoding
  const geocodePostalCode = async (postalCode: string) => {
    if (!isLoaded || !postalCode || !window.google) return;

    console.log("Attempting to geocode postal code:", postalCode);
    
    try {
      const geocoder = new google.maps.Geocoder();
      
      geocoder.geocode(
        { 
          address: postalCode, 
          componentRestrictions: { country: "gr" } 
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            processGeocodingResults(results[0], postalCode);
          } else {
            console.error("Failed to geocode postal code:", status);
          }
        }
      );
    } catch (error) {
      console.error("Error in postal code geocoding:", error);
    }
  };
  
  // Process geocoding results in a consistent way
  const processGeocodingResults = (result: google.maps.GeocoderResult, originalInput: string) => {
    const location = result.geometry.location;
    const lat = location.lat();
    const lng = location.lng();
    let postalCode = "";
    let cityName = "";
    let regionName = "";
    
    // Extract information from address components
    if (result.address_components) {
      for (const component of result.address_components) {
        if (component.types.includes("postal_code")) {
          postalCode = component.long_name;
        }
        if (component.types.includes("locality")) {
          cityName = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          regionName = component.long_name;
        }
      }
    }
    
    // If we don't have a postal code but the original input looks like one, use it
    if (!postalCode && /^\d{5}$/.test(originalInput)) {
      postalCode = originalInput;
    }
    
    console.log(`Geocoded: ${originalInput} to: ${result.formatted_address}, Lat: ${lat}, Lng: ${lng}, Postal: ${postalCode}, City: ${cityName}, Region: ${regionName}`);
    
    // Update the input with formatted address
    if (result.formatted_address) {
      onChange(result.formatted_address);
    } else {
      // If no formatted address, keep original or use something descriptive
      onChange(originalInput);
    }
    
    // Store location details for possible fallback matching
    setLocationDetails({
      cityName,
      regionName
    });
    
    // Call the location select callback
    if (onLocationSelect) {
      onLocationSelect(lat, lng, postalCode);
    }
    
    // Save postal code for future use
    if (postalCode) {
      setManualPostalCode(postalCode);
    }
  };

  // Handle blur event to check for any input
  const handleBlur = () => {
    const currentValue = value.trim();
    
    if (currentValue.length > 0) {
      const postalCodeRegex = /^\d{5}$/;
      
      if (postalCodeRegex.test(currentValue)) {
        // If looks like a postal code
        geocodePostalCode(currentValue);
      } else if (currentValue.length > 2) {
        // If looks like a city name or address
        geocodeAddress(currentValue);
      }
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      // Prevent form submission on Enter key
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    />
  );
};

export default AddressAutocomplete;
